<!--
 * Path: packages/frontend/src/features/browser/forms/timeline/TimelineRead.vue
 * Task: Read-only visualization of connected entities as a horizontal process timeline.
 * Update: Context-aware click handling. Opens in pane-right if primary, opens in Modal if already in pane-right.
-->
<template>
  <div v-if="allTimelineItems.length === 0" class="text-caption text-medium-emphasis font-italic mt-2">
    No timeline events available.
  </div>
  <div v-else class="w-100 overflow-x-auto pt-3 pb-1">
    <!-- Horizontal Timeline (side="end" forces all labels underneath the line) -->
    <v-timeline
      direction="horizontal"
      align="center"
      side="end"
      class="w-100"
      line-thickness="2"
    >
      <v-timeline-item
        v-for="item in allTimelineItems"
        :key="item.id"
        :dot-color="item.color || 'primary'"
        :size="item.isActive ? 'small' : 'x-small'"
        :elevation="item.isActive ? 2 : 0"
        fill-dot
      >
        <!-- Hit-Area for Tooltip filling the entire dot -->
        <template v-slot:icon>
          <v-tooltip location="top" open-delay="100" offset="4">
            <template v-slot:activator="{ props }">
              <div v-bind="props" class="w-100 h-100 d-flex align-center justify-center" style="cursor: help;">
                <v-icon v-if="item.isActive" color="white" size="14">{{ item.icon || 'mdi-shape' }}</v-icon>
              </div>
            </template>
            <span class="font-weight-medium">{{ item.title }}</span>
          </v-tooltip>
        </template>

        <!-- Text Content below the dot -->
        <div
          class="d-flex flex-column align-center mt-2"
          :class="{ 'hover-pointer': item.isActive }"
          @click="item.isActive ? openEntity(item.id) : null"
        >
          <template v-if="item.isActive">
            <span class="text-caption font-weight-bold text-high-emphasis text-center" style="line-height: 1.2; min-width: 80px; max-width: 120px; white-space: normal;">
              {{ item.title }}
            </span>
          </template>
          <!-- Invisible spacer with tooltip to guarantee even distribution and larger hit-area -->
          <div v-else style="min-width: 40px; height: 16px; cursor: help; margin-top: -8px;">
            <v-tooltip activator="parent" location="bottom" open-delay="100" offset="4">
              <span class="font-weight-medium">{{ item.title }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useUiStore } from '@/store/ui';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';

const props = defineProps<{ field: any }>();

const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const uiStore = useUiStore();
const { getSelectOptions } = useSelectOptions();

const formContext = inject(FormContextKey, null);
const isSecondaryContext = computed<boolean>(() => formContext ? formContext.isSecondary : false);

// Context-Aware Entity Resolution
const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

// Resolve ALL possible timeline events (e.g. all phases) and mark the active ones
const allTimelineItems = computed(() => {
  if (!props.field.entitySource || !datastoreStore.allEntities) return [];

  // 1. Fetch ALL available options for this timeline from the global schema
  const options = getSelectOptions(props.field.entitySource, props.field.id);

  // 2. Determine which ones are currently assigned to the active entity
  const rawTargetIds = activeEntity.value ? (activeEntity.value as any)[props.field.id] : null;
  const activeIds = Array.isArray(rawTargetIds) ? rawTargetIds : (rawTargetIds ? [rawTargetIds] : []);

  // 3. Map status and sort them sequentially (by rank)
  return options.map(opt => {
    const isActive = activeIds.includes(opt.id);
    return {
      ...opt,
      isActive
    };
  }).sort((a, b) => (a.rank || 0) - (b.rank || 0));
});

const openEntity = (id: string) => {
  if (isSecondaryContext.value) {
    // Wenn wir bereits im Secondary Panel sind, nutze das Preview Modal
    workspaceStore.setPreviewEntity(id);
  } else {
    // Wenn wir in der Hauptansicht sind, öffne das Secondary Panel
    workspaceStore.setSecondarySelectedEntity(id);
    uiStore.panels.showSecondaryDetails = true;
  }
};
</script>

<style scoped>
.hover-pointer {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.hover-pointer:hover {
  opacity: 0.8;
}

.hover-pointer:hover span {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: underline;
}

/* Optimierung des Grid-Gaps für flüssige 100% Breitennutzung ohne Überlauf */
:deep(.v-timeline--horizontal) {
  grid-column-gap: 0 !important;
}
</style>
